import { useNode } from "@craftjs/core";
import React, { useMemo } from "react";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { isUsableColor } from "../../../../utils/chartColor";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import GraphChart from "./GraphChart";
import GraphSettings from "./GraphSettings";

/**
 * Graph block — a Wheel-Builder content block that renders a chart from a small
 * per-datapoint dataset. Registered as a real craft.js node (see resolver in
 * pages/SegmentBuilder/Editor.jsx + toolbox entry in constants/segmentTools).
 *
 * Design source of truth: design-handoff/concepts/builder-blocks
 * (01-graph-block-editor + 06 render fix). Chart types Bar/Line/Area/Pie/Donut/
 * Radar; each datapoint carries its own color via the universal color
 * component (ColorPopup). Colors are stored as palette refs (suggested_color_n)
 * so the chart auto-themes to the Segment/Wheel palette, and are resolved to
 * concrete CSS colors here for the SVG.
 *
 * gordon-0915-graph-block-remove-scatter-chart-type: Scatter was removed from
 * this list — the block stores one number per data point, so a scatter plot
 * is just a Line chart without the connecting line. A block saved with the
 * old chartType: "scatter" still renders (as Line — see GraphChart.jsx); this
 * list only controls what's offered going forward.
 */

export const CHART_TYPES = [
  { key: "bar", label: "Bar" },
  { key: "line", label: "Line" },
  { key: "area", label: "Area" },
  { key: "pie", label: "Pie" },
  { key: "donut", label: "Donut" },
  { key: "radar", label: "Radar" },
];

// gordon-0915-graph-block-pie-donut-white-wedge-blurry-labels-legend-contrast:
// a datapoint must never default to white/transparent. When its stored color
// reference is missing, or resolves through a wiped palette entry, derive a
// real fallback (segment colour, then wheel palette) shaded per index so two
// missing-color datapoints in the same chart stay distinguishable instead of
// collapsing onto one identical swatch. Exported as a pure function so this
// resolution logic is unit-testable without mounting the craft.js node tree.
export const resolveGraphData = (data, { getPaletteColor, getFallbackColor }) =>
  (Array.isArray(data) ? data : []).map((d, i) => {
    const preferred = d?.color ? getPaletteColor(d.color) : undefined;
    return {
      label: d?.label ?? "",
      value: d?.value,
      color: isUsableColor(preferred) ? preferred : getFallbackColor(i),
    };
  });

export const Graph = ({ title, chartType, data, margin }) => {
  const {
    connectors: { connect, drag },
  } = useNode();
  const { getPaletteColor, getFallbackColor } = usePaletteColor();

  const resolvedData = useMemo(
    () => resolveGraphData(data, { getPaletteColor, getFallbackColor }),
    [data, getPaletteColor, getFallbackColor]
  );

  const marginStyle = margin
    ? `${margin.top || 0}px ${margin.right || 0}px ${margin.bottom || 0}px ${margin.left || 0}px`
    : undefined;

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <div style={{ padding: marginStyle }}>
          <div style={{ padding: "8px 4px 6px", width: "100%", boxSizing: "border-box" }}>
            {title ? (
              <div
                style={{
                  color: "#123051",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 6,
                  textAlign: "center",
                }}
              >
                {title}
              </div>
            ) : null}
            <GraphChart chartType={chartType || "bar"} data={resolvedData} />
          </div>
        </div>
      </BlockIndicator>
    </div>
  );
};

// fix-kelly-0930-P0 (Kelly, VP Sales, 2026-09-30): "Newly added Graph and
// Table blocks must start EMPTY (no demo rows 'UX Lead/Contoso', no demo
// bars) -- show a clean empty state with a hint to add data." GraphChart.jsx
// already renders "Add data points to see your chart" whenever `data` is
// empty, so an empty default here is all that's needed.
export const GraphDefaultProps = {
  title: "",
  chartType: "bar",
  data: [],
  // fix-gordon-0930-P1-block-spacing-controls-every-block-addendum-1458: Graph
  // had no margin prop at all (settings pane offered no way to space it from
  // neighboring blocks). Left/Right stay 0 — the settings control hides those
  // two inputs (hideHorizontal, see GraphSettings.jsx) since Graph already
  // pads itself horizontally (padding: "8px 4px 6px" above).
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
};

Graph.craft = {
  displayName: "GraphWidget",
  props: GraphDefaultProps,
  related: {
    settings: GraphSettings,
  },
};

export default Graph;
